import { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import clsx from "clsx";
import { ArrowRight, Calendar, Eye, Film, Lock, Search, Shield, Users, X } from "lucide-react";
import { api } from "../../lib/api";
import type { SearchSort, VideoOut } from "../../lib/types";
import { useI18n } from "../../i18n";
import { formatDateTime, formatDuration } from "../../lib/format";
import { Button, EmptyState, Input, Modal, Select, Spinner } from "../../components/ui";

export function SearchDialog({
  open,
  onClose,
  initialCommunity,
  initialTab = "videos",
}: {
  open: boolean;
  onClose: () => void;
  initialCommunity?: string | null;
  initialTab?: "videos" | "communities";
}) {
  const { t, lang } = useI18n();

  const [activeTab, setActiveTab] = useState<"videos" | "communities">(initialTab);

  // Sync activeTab when dialog opens with initialTab
  useEffect(() => {
    if (open) {
      setActiveTab(initialTab);
    }
  }, [open, initialTab]);

  // Video search state
  const [keyword, setKeyword] = useState("");
  const [community, setCommunity] = useState<string>(initialCommunity ?? "");
  const [uploadedFrom, setUploadedFrom] = useState("");
  const [uploadedTo, setUploadedTo] = useState("");
  const [sort, setSort] = useState<SearchSort>("latest");
  const [submittedQuery, setSubmittedQuery] = useState<{
    q: string;
    community: string;
    uploadedFrom: string;
    uploadedTo: string;
    sort: SearchSort;
  }>({
    q: "",
    community: initialCommunity ?? "",
    uploadedFrom: "",
    uploadedTo: "",
    sort: "latest",
  });

  // Debounced auto-search for videos (300ms)
  useEffect(() => {
    const timer = setTimeout(() => {
      setSubmittedQuery((prev) => {
        if (prev.q === keyword.trim()) return prev;
        return { ...prev, q: keyword.trim() };
      });
    }, 300);
    return () => clearTimeout(timer);
  }, [keyword]);

  // Communities search state & debounce (300ms)
  const [communityKeyword, setCommunityKeyword] = useState("");
  const [debouncedCommunityKeyword, setDebouncedCommunityKeyword] = useState("");

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedCommunityKeyword(communityKeyword.trim());
    }, 300);
    return () => clearTimeout(timer);
  }, [communityKeyword]);

  // Load communities for filter dropdown only when dialog is open
  const communitiesQuery = useQuery({
    queryKey: ["communities"],
    queryFn: () => api.communities.list(),
    enabled: open,
    staleTime: 5 * 60 * 1000,
  });

  // Search communities query
  const searchedCommunitiesQuery = useQuery({
    queryKey: ["search-communities", debouncedCommunityKeyword],
    queryFn: () => api.communities.list(debouncedCommunityKeyword || null),
    enabled: open && activeTab === "communities",
    staleTime: 30 * 1000,
  });

  const searchQuery = useInfiniteQuery({
    queryKey: [
      "search-videos",
      submittedQuery.q,
      submittedQuery.community,
      submittedQuery.uploadedFrom,
      submittedQuery.uploadedTo,
      submittedQuery.sort,
    ],
    queryFn: ({ pageParam }) =>
      api.videos.search({
        q: submittedQuery.q || null,
        community: submittedQuery.community || null,
        uploaded_from: submittedQuery.uploadedFrom || null,
        uploaded_to: submittedQuery.uploadedTo || null,
        sort: submittedQuery.sort,
        cursor: pageParam,
        limit: 12,
      }),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.next_cursor,
    enabled: open && activeTab === "videos",
  });

  const videos: VideoOut[] = useMemo(
    () => searchQuery.data?.pages.flatMap((page) => page.items) ?? [],
    [searchQuery.data],
  );

  const handleSearch = (e?: React.FormEvent) => {
    e?.preventDefault();
    setSubmittedQuery({
      q: keyword.trim(),
      community,
      uploadedFrom,
      uploadedTo,
      sort,
    });
  };

  const handleReset = () => {
    setKeyword("");
    setCommunity("");
    setUploadedFrom("");
    setUploadedTo("");
    setSort("latest");
    setSubmittedQuery({
      q: "",
      community: "",
      uploadedFrom: "",
      uploadedTo: "",
      sort: "latest",
    });
  };

  return (
    <Modal open={open} onClose={onClose} title={t("search.title")}>
      <div className="space-y-4">
        {/* Tab switcher: Videos vs Communities */}
        <div className="flex border-b border-[var(--color-hairline)]">
          <button
            type="button"
            onClick={() => setActiveTab("videos")}
            className={clsx(
              "flex items-center gap-2 border-b-2 px-4 py-2.5 text-sm font-medium transition",
              activeTab === "videos"
                ? "border-brand-500 text-brand-400 font-semibold"
                : "border-transparent text-[var(--color-ink-muted)] hover:text-[var(--color-ink)]",
            )}
          >
            <Film className="size-4" />
            <span>{t("search.tabVideos")}</span>
          </button>
          <button
            type="button"
            onClick={() => setActiveTab("communities")}
            className={clsx(
              "flex items-center gap-2 border-b-2 px-4 py-2.5 text-sm font-medium transition",
              activeTab === "communities"
                ? "border-brand-500 text-brand-400 font-semibold"
                : "border-transparent text-[var(--color-ink-muted)] hover:text-[var(--color-ink)]",
            )}
          >
            <Users className="size-4" />
            <span>{t("search.tabCommunities")}</span>
          </button>
        </div>

        {/* VIDEOS TAB */}
        {activeTab === "videos" && (
          <div className="space-y-4">
            {/* Search controls */}
            <form onSubmit={handleSearch} className="space-y-3">
              <div className="relative flex items-center">
                <Search className="pointer-events-none absolute start-3.5 size-4.5 text-[var(--color-ink-faint)]" />
                <Input
                  value={keyword}
                  onChange={(e) => setKeyword(e.target.value)}
                  placeholder={t("search.placeholder")}
                  className="ps-10 pe-9"
                  dir="auto"
                  data-autofocus
                />
                {keyword && (
                  <button
                    type="button"
                    onClick={() => setKeyword("")}
                    className="absolute end-3 text-[var(--color-ink-faint)] hover:text-[var(--color-ink)]"
                  >
                    <X className="size-4" />
                  </button>
                )}
              </div>

              <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
                {/* Community filter */}
                <div>
                  <label className="mb-1 block text-xs font-medium text-[var(--color-ink-muted)]">
                    {t("video.community")}
                  </label>
                  <Select
                    value={community}
                    onChange={(e) => setCommunity(e.target.value)}
                  >
                    <option value="">{t("community.all")}</option>
                    {communitiesQuery.data?.map((c) => (
                      <option key={c.id} value={c.slug}>
                        {c.name}
                      </option>
                    ))}
                  </Select>
                </div>

                {/* Uploaded From */}
                <div>
                  <label className="mb-1 block text-xs font-medium text-[var(--color-ink-muted)]">
                    {t("search.from")}
                  </label>
                  <Input
                    type="date"
                    value={uploadedFrom}
                    onChange={(e) => setUploadedFrom(e.target.value)}
                    className="text-xs"
                  />
                </div>

                {/* Uploaded To */}
                <div>
                  <label className="mb-1 block text-xs font-medium text-[var(--color-ink-muted)]">
                    {t("search.to")}
                  </label>
                  <Input
                    type="date"
                    value={uploadedTo}
                    onChange={(e) => setUploadedTo(e.target.value)}
                    className="text-xs"
                  />
                </div>
              </div>

              <div className="flex flex-wrap items-center justify-between gap-2 pt-1">
                <div className="flex items-center gap-2">
                  <span className="text-xs text-[var(--color-ink-faint)]">{t("search.sort")}:</span>
                  <div className="inline-flex rounded-lg bg-[var(--color-surface-2)] p-0.5 text-xs">
                    {(
                      [
                        { key: "latest", labelKey: "search.sort.latest" },
                        { key: "oldest", labelKey: "search.sort.oldest" },
                        { key: "popular", labelKey: "search.sort.popular" },
                      ] as const
                    ).map((opt) => (
                      <button
                        key={opt.key}
                        type="button"
                        onClick={() => {
                          setSort(opt.key);
                          setSubmittedQuery((prev) => ({ ...prev, sort: opt.key }));
                        }}
                        className={clsx(
                          "rounded-md px-2.5 py-1 font-medium transition",
                          sort === opt.key
                            ? "bg-brand-500 text-white shadow-xs"
                            : "text-[var(--color-ink-muted)] hover:text-[var(--color-ink)]",
                        )}
                      >
                        {t(opt.labelKey)}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <Button type="button" variant="ghost" size="sm" onClick={handleReset}>
                    {t("search.clear")}
                  </Button>
                  <Button type="submit" size="sm">
                    <Search className="size-3.5" />
                    {t("search.title")}
                  </Button>
                </div>
              </div>
            </form>

            <div className="border-t border-[var(--color-hairline)] pt-3">
              {searchQuery.isPending ? (
                <div className="grid min-h-48 place-items-center">
                  <Spinner className="size-6" />
                </div>
              ) : videos.length === 0 ? (
                <EmptyState
                  icon={<Film className="size-8" />}
                  title={t("search.noResults")}
                  hint={t("search.noResultsHint")}
                />
              ) : (
                <div className="no-scrollbar max-h-[55vh] space-y-2 overflow-y-auto pe-1">
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    {videos.map((video) => (
                      <Link
                        key={video.id}
                        to={"/v/" + video.id}
                        onClick={onClose}
                        className="group flex gap-3 rounded-xl border border-[var(--color-hairline)] bg-[var(--color-surface-2)]/40 p-2.5 transition hover:border-brand-500/40 hover:bg-[var(--color-surface-2)]"
                      >
                        <div className="relative aspect-[9/16] h-24 shrink-0 overflow-hidden rounded-lg bg-black">
                          {video.thumbnail_url ? (
                            <img
                              src={video.thumbnail_url}
                              alt={video.title}
                              className="h-full w-full object-cover transition group-hover:scale-105"
                            />
                          ) : (
                            <div className="grid h-full w-full place-items-center text-white/40">
                              <Film className="size-5" />
                            </div>
                          )}
                          {video.duration ? (
                            <span className="absolute bottom-1 end-1 rounded bg-black/75 px-1 py-0.5 text-[10px] font-medium text-white tabular-nums">
                              {formatDuration(video.duration)}
                            </span>
                          ) : null}
                        </div>

                        <div className="flex min-w-0 flex-1 flex-col justify-between py-0.5">
                          <div>
                            <h4 className="line-clamp-2 text-xs font-semibold leading-tight group-hover:text-brand-300" dir="auto">
                              {video.title}
                            </h4>
                            <p className="mt-1 truncate text-[11px] text-[var(--color-ink-faint)]">
                              @{video.user.username}
                            </p>
                          </div>

                          <div className="space-y-1 text-[11px] text-[var(--color-ink-muted)]">
                            <div className="flex items-center gap-1.5 truncate">
                              <span className="rounded bg-brand-500/15 px-1.5 py-0.5 font-medium text-brand-300">
                                {video.community.name}
                              </span>
                            </div>
                            <div className="flex items-center gap-3 text-[10px] text-[var(--color-ink-faint)]">
                              <span className="flex items-center gap-1">
                                <Eye className="size-3" />
                                {video.view_count}
                              </span>
                              <span className="flex items-center gap-1">
                                <Calendar className="size-3" />
                                {formatDateTime(video.created_at, lang)}
                              </span>
                            </div>
                          </div>
                        </div>
                      </Link>
                    ))}
                  </div>

                  {searchQuery.hasNextPage && (
                    <div className="pt-2 text-center">
                      <Button
                        variant="ghost"
                        size="sm"
                        loading={searchQuery.isFetchingNextPage}
                        onClick={() => void searchQuery.fetchNextPage()}
                      >
                        {t("common.more")}
                      </Button>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {/* COMMUNITIES TAB */}
        {activeTab === "communities" && (
          <div className="space-y-4">
            {/* Community search input */}
            <div className="relative flex items-center">
              <Search className="pointer-events-none absolute start-3.5 size-4.5 text-[var(--color-ink-faint)]" />
              <Input
                value={communityKeyword}
                onChange={(e) => setCommunityKeyword(e.target.value)}
                placeholder={t("search.communitiesPlaceholder")}
                className="ps-10 pe-9"
                dir="auto"
                data-autofocus
              />
              {communityKeyword && (
                <button
                  type="button"
                  onClick={() => setCommunityKeyword("")}
                  className="absolute end-3 text-[var(--color-ink-faint)] hover:text-[var(--color-ink)]"
                >
                  <X className="size-4" />
                </button>
              )}
            </div>

            {/* Community search results */}
            <div className="border-t border-[var(--color-hairline)] pt-3">
              {searchedCommunitiesQuery.isPending ? (
                <div className="grid min-h-48 place-items-center">
                  <Spinner className="size-6" />
                </div>
              ) : !searchedCommunitiesQuery.data || searchedCommunitiesQuery.data.length === 0 ? (
                <EmptyState
                  icon={<Users className="size-8" />}
                  title={t("search.noCommunities")}
                  hint={t("search.noCommunitiesHint")}
                />
              ) : (
                <div className="no-scrollbar max-h-[55vh] space-y-2.5 overflow-y-auto pe-1">
                  {searchedCommunitiesQuery.data.map((comm) => (
                    <div
                      key={comm.id}
                      className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 rounded-xl border border-[var(--color-hairline)] bg-[var(--color-surface-2)]/40 p-3.5 transition hover:border-brand-500/40 hover:bg-[var(--color-surface-2)]"
                    >
                      <div className="min-w-0 flex-1 space-y-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="text-sm font-semibold text-[var(--color-ink)]">
                            {comm.name}
                          </span>
                          <span
                            className={clsx(
                              "rounded-full px-2 py-0.5 text-[10px] font-semibold",
                              comm.access_type === "PUBLIC"
                                ? "bg-emerald-500/15 text-emerald-400"
                                : comm.access_type === "RESTRICTED"
                                  ? "bg-amber-500/15 text-amber-400"
                                  : "bg-rose-500/15 text-rose-400",
                            )}
                          >
                            {comm.access_type === "RESTRICTED" ? (
                              <span className="inline-flex items-center gap-1">
                                <Shield className="size-2.5" />
                                {t("community.access.restricted")}
                              </span>
                            ) : comm.access_type === "PRIVATE" ? (
                              <span className="inline-flex items-center gap-1">
                                <Lock className="size-2.5" />
                                {t("community.access.private")}
                              </span>
                            ) : (
                              t("community.access.public")
                            )}
                          </span>
                          <span className="rounded-full bg-brand-500/15 px-2 py-0.5 text-[10px] font-medium text-brand-300">
                            {comm.video_count} {t("search.videoCount")}
                          </span>
                          {comm.owner && (
                            <span className="text-xs text-[var(--color-ink-faint)]">
                              by @{comm.owner.username}
                            </span>
                          )}
                        </div>
                        {comm.description && (
                          <p
                            className="line-clamp-2 text-xs text-[var(--color-ink-muted)]"
                            dir="auto"
                          >
                            {comm.description}
                          </p>
                        )}
                      </div>

                      <Link
                        to={`/?community=${comm.slug}`}
                        onClick={onClose}
                        className="inline-flex shrink-0 items-center justify-center gap-1.5 rounded-xl bg-brand-500/15 px-3 py-1.5 text-xs font-semibold text-brand-300 transition hover:bg-brand-500/25"
                      >
                        <span>{t("search.browseCommunity")}</span>
                        <ArrowRight className="size-3.5" />
                      </Link>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
}
